import React, { useState, useEffect } from 'react';
import { Bell, Menu, Check, Trash2, ExternalLink, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { api } from '@/api/apiClient';
import { createPageUrl } from '@/utils';
import { useTheme } from '@/hooks/useTheme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import GlobalSearch from '@/components/layout/GlobalSearch';
import { markNotificationRead, markAllNotificationsRead, deleteNotification } from '@/components/utils/notificationHelpers';

export default function TopBar({ user, onMenuToggle, showMenuButton }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isDark, toggleTheme } = useTheme();

  const initials = user?.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase() || 'U';

  useEffect(() => {
    if (user?.email) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000); // Refresh every 30s
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      const data = await api.entities.Notification.filter({
        user_id: user.id
      });
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    await markNotificationRead(notification.id);
    loadNotifications();
    
    if (notification.action_url) {
      globalThis.location.href = createPageUrl(notification.action_url);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead(user.email);
    loadNotifications();
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    await deleteNotification(notificationId);
    loadNotifications();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return 'âœ…';
      case 'warning': return 'âš ï¸';
      case 'error': return 'âŒ';
      case 'action_required': return 'ðŸ””';
      default: return 'â„¹ï¸';
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'urgent') return 'bg-red-50 border-red-200';
    switch (type) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-amber-50 border-amber-200';
      case 'error': return 'bg-red-50 border-red-200';
      case 'action_required': return 'bg-blue-50 border-blue-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  const getRoleBadge = () => {
    const roleMap = {
      admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
      sub_admin: { label: 'Sub-Admin', color: 'bg-blue-100 text-blue-700' },
      employer_hr: { label: 'HR Manager', color: 'bg-amber-100 text-amber-700' },
      employee: { label: 'Employee', color: 'bg-emerald-100 text-emerald-700' },
    };
    const role = roleMap[user?.user_role] || roleMap.employee;
    return <Badge className={`${role.color} font-medium`}>{role.label}</Badge>;
  };

  return (
    <header className="h-16 bg-background border-b border-border flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30">
      <div className="flex items-center gap-4">
        {showMenuButton && (
          <Button variant="ghost" size="icon" onClick={onMenuToggle} className="lg:hidden">
            <Menu className="w-5 h-5" />
          </Button>
        )}
        <div className="hidden lg:block w-80">
          <GlobalSearch userRole={user?.user_role} />
        </div>
      </div>

      <div className="flex items-center gap-4">

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDark ? 'Light mode' : 'Dark mode'}
        >
          {isDark ? (
            <Sun className="w-5 h-5 text-muted-foreground" />
          ) : (
            <Moon className="w-5 h-5 text-muted-foreground" />
          )}
        </Button>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5 text-muted-foreground" />
              {unreadCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[min(24rem,calc(100vw-1rem))]">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="h-6 text-xs">
                  <Check className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <ScrollArea className="h-96">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <div className="space-y-1 p-1">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        getNotificationColor(notification.type, notification.priority)
                      } ${!notification.is_read ? 'font-medium' : 'opacity-70'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                            <span className="text-sm font-semibold">{notification.title}</span>
                            {!notification.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{notification.message}</p>
                          {notification.action_label && (
                            <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                              <ExternalLink className="w-3 h-3" />
                              {notification.action_label}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground/70 mt-1">
                            {new Date(notification.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground/70 hover:text-red-500"
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-3 px-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.profile_picture_url} />
                <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-teal-500 text-white text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-foreground">{user?.full_name || 'User'}</p>
                {getRoleBadge()}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => window.location.href = createPageUrl('AccountSettings')}>
              Account Settings
            </DropdownMenuItem>
            {user?.employee_id && (
              <DropdownMenuItem onClick={() => window.location.href = createPageUrl('EmployeeProfile')}>
                Profile Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => window.location.href = createPageUrl('Help')}>
              Help & Support
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-red-600 cursor-pointer"
              onClick={async () => {
                try {
                  await api.auth.logout();
                  globalThis.location.href = '/';
                } catch (error) {
                  console.error('Logout failed:', error);
                  globalThis.location.href = '/';
                }
              }}
            >
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}